<template>
  <div class="pet-name-selector-container">
    <div class="selector-header">
      <h1 class="selector-title">
        <template v-if="isPendingUpload">
          Name This Pet
        </template>
        <template v-else-if="isNamingExisting">
          Assign Pet Names
        </template>
        <template v-else>
          Multiple Pets: Match Your Pet
        </template>
      </h1>
      <p v-if="(isNamingExisting || (!isPendingUpload && !isNamingExisting)) && totalImages > 1" class="progress-info">
        Image {{ currentIndex + 1 }} of {{ totalImages }}
      </p>
      <p v-if="isPendingUpload" class="upload-info">
        Choose a name for this pet to complete the upload
      </p>
      <p v-if="isNamingExisting" class="naming-info">
        Please assign a pet name to each uploaded image
      </p>
    </div>
    
    <div class="pet-display">
      <div class="pet-image-container">
        <img 
          :src="petImage || '/assets/images/examples/default-pet.jpg'" 
          alt="Your pet"
          class="pet-image"
          @error="handleImageError"
        />
      </div>
    </div>
    
    <div class="name-options">
      <div v-if="availableNames.length === 0" class="no-pets-message">
        <p>No pets added yet. Please add pets first.</p>
        <button class="add-pets-button" @click="goBack">Add Pets</button>
      </div>
      <template v-else>
        <button 
          v-for="name in availableNames" 
          :key="name"
          class="name-button"
          :class="{ selected: selectedName === name }"
          @click="selectName(name)"
        >
          {{ name }}
        </button>
      </template>
    </div>
    
    <div class="navigation-container">
      <button class="nav-arrow left" @click="goBack">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      
      <div class="selection-info">
        <template v-if="availableNames.length === 0">
          Please add pets first
        </template>
        <template v-else-if="isPendingUpload">
          Select a pet name to complete the upload
        </template>
        <template v-else-if="isNamingExisting">
          Choose which pet this image represents
        </template>
        <template v-else>
          Choose a name that matches your pet
        </template>
      </div>
      
      <button class="nav-arrow right" @click="proceed" :disabled="!selectedName || availableNames.length === 0">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PetNameSelector',
  props: {
    petImage: {
      type: String,
      default: ''
    },
    availableNames: {
      type: Array,
      default: () => []
    },
    currentIndex: {
      type: Number,
      default: 0
    },
    totalImages: {
      type: Number,
      default: 1
    },
    isPendingUpload: {
      type: Boolean,
      default: false
    },
    isNamingExisting: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedName: ''
    };
  },
  methods: {
    selectName(name) {
      this.selectedName = name;
    },
    proceed() {
      if (this.selectedName) {
        this.$emit('nameSelected', this.selectedName);
      }
    },
    goBack() {
      this.$emit('back');
    },
    handleImageError(event) {
      // Fallback to default image if the pet image fails to load
      event.target.src = '/assets/images/examples/default-pet.jpg';
    }
  }
};
</script>

<style scoped>
.pet-name-selector-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.selector-header {
  text-align: center;
  margin-bottom: 3vw;
}

.selector-title {
  font-size: 2.5vw;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.progress-info {
  font-size: 1.2vw;
  color: #666;
  margin: 0.5vw 0 0 0;
  font-weight: 400;
}

.upload-info {
  font-size: 1.2vw;
  color: #f39c12;
  margin: 0.5vw 0 0 0;
  font-weight: 500;
}

.naming-info {
  font-size: 1.2vw;
  color: #2c3e50;
  margin: 0.5vw 0 0 0;
  font-weight: 500;
}

.pet-display {
  margin-bottom: 3vw;
}

.pet-image-container {
  width: 12vw;
  height: 12vw;
  border-radius: 1vw;
  overflow: hidden;
  box-shadow: 0 0.2vw 1vw rgba(0,0,0,0.1);
}

.pet-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.name-options {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1vw;
  margin-bottom: 3vw;
  width: 100%;
  max-width: 30vw;
}

.name-button {
  padding: 1vw 2vw;
  border: 0.1vw solid #ddd;
  border-radius: 2vw;
  background: white;
  color: #666;
  cursor: pointer;
  font-size: 1vw;
  transition: all 0.3s ease;
  text-align: center;
}

.name-button:hover {
  border-color: #999;
  background: #f8f9fa;
}

.name-button.selected {
  background: #87CEEB;
  color: white;
  border-color: #87CEEB;
}

.no-pets-message {
  text-align: center;
  padding: 2vw;
  color: #666;
}

.no-pets-message p {
  margin-bottom: 1.5vw;
  font-size: 1vw;
}

.add-pets-button {
  padding: 1vw 2vw;
  background: #87CEEB;
  color: white;
  border: none;
  border-radius: 2vw;
  cursor: pointer;
  font-size: 1vw;
  transition: background 0.3s ease;
}

.add-pets-button:hover {
  background: #5f9ea0;
}

.navigation-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 2vw;
}

.nav-arrow {
  width: 3vw;
  height: 3vw;
  border-radius: 50%;
  border: 0.15vw solid #333;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.nav-arrow:hover:not(:disabled) {
  background: #f5f5f5;
  transform: scale(1.1);
}

.nav-arrow:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.nav-arrow svg {
  width: 1.5vw;
  height: 1.5vw;
}

.selection-info {
  font-size: 1.1vw;
  color: #666;
  text-align: center;
}

/* Mobile styles */
@media (max-width: 768px) {
  .pet-name-selector-container {
    padding: 0;
  }

  .selector-title {
    font-size: 6vw;
    margin-bottom: 2vw;
  }

  .progress-info {
    font-size: 3.5vw;
    margin: 2vw 0 0 0;
  }

  .upload-info {
    font-size: 3.5vw;
    margin: 2vw 0 0 0;
  }

  .naming-info {
    font-size: 3.5vw;
    margin: 2vw 0 0 0;
  }

  .pet-image-container {
    width: 35vw;
    height: 35vw;
    border-radius: 2vw;
  }

  .name-options {
    gap: 3vw;
    max-width: 80vw;
    margin-bottom: 6vw;
  }

  .name-button {
    padding: 3vw 6vw;
    font-size: 3.5vw;
    border-radius: 5vw;
  }

  .navigation-container {
    padding: 0 4vw;
  }

  .nav-arrow {
    width: 8vw;
    height: 8vw;
    border: 0.3vw solid #333;
  }

  .nav-arrow svg {
    width: 4vw;
    height: 4vw;
  }

  .selection-info {
    font-size: 3vw;
  }

  .no-pets-message {
    padding: 4vw;
  }

  .no-pets-message p {
    margin-bottom: 4vw;
    font-size: 3.5vw;
  }

  .add-pets-button {
    padding: 3vw 6vw;
    font-size: 3.5vw;
    border-radius: 5vw;
  }
}
</style> 